"use client";

import React from "react";

export interface MilestoneData {
    period: string;
    phase: string;
    title: string;
    description: string;
}

export interface MemorylayerTimelineBlockProps {
    tag?: string;
    title?: string;
    milestones?: MilestoneData[];
}

export default function MemorylayerTimelineBlock({
    tag = "Project Timeline",
    title = "Milestones in Development",
    milestones = [
        {
            period: "Month 01",
            phase: "Phase 01: Discovery",
            title: "Analyzing context limitations",
            description: "Deep dive into model baseline thresholds, mapping out persistent memories and vector data storage requirements."
        },
        {
            period: "Month 02",
            phase: "Phase 02: Sandboxing",
            title: "Simulated preset testing",
            description: "Crafting fine-tuned prompt directives, managing formatting tone templates, and verifying baseline LLM completions."
        },
        {
            period: "Month 03",
            phase: "Phase 03: Vector RAG Core",
            title: "Database index setup",
            description: "Configuring PostgreSQL pgvector, building index query loops, and minimizing query matching latency under 15ms."
        },
        {
            period: "Month 04-05",
            phase: "Phase 04: Sandbox UI",
            title: "Frontend dashboard",
            description: "Building the customizer workspace console, hooking up visual sliders, custom preset triggers, and live logs preview."
        },
        {
            period: "Month 06",
            phase: "Phase 05: Tuning",
            title: "Production hardening",
            description: "Conducting automated jailbreak attack tests, setting up load balance routers, and launching to production."
        }
    ]
}: MemorylayerTimelineBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0F0F12] border-t border-b border-zinc-900 w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="max-w-2xl mb-16">
                    {tag && (
                        <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest block mb-4">// {tag}</span>
                    )}
                    <h2 className="text-4xl md:text-5xl font-light text-white leading-tight font-sans">
                        {title}
                    </h2>
                </div>

                {/* Timeline Grid */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-8 relative">
                    {/* Horizontal connector line on larger screens */}
                    <div className="hidden md:block absolute top-[13px] left-[40px] right-[40px] h-[1px] bg-zinc-850 z-0" />
                    
                    {milestones.map((milestone, idx) => (
                        <div key={idx} className="space-y-4 relative z-10">
                            {/* Dot / Indicator */}
                            <div className="flex items-center gap-3 md:flex-col md:items-start">
                                <div className="w-7 h-7 rounded-full bg-[#0F0F12] border-2 border-purple-500 flex items-center justify-center flex-shrink-0">
                                    <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                                </div>
                                <span className="text-xs font-mono text-purple-400 uppercase tracking-wider md:mt-2">
                                    {milestone.period}
                                </span>
                            </div>

                            {/* Milestone Content Card */}
                            <div className="bg-[#121214] border border-zinc-850 p-5 rounded-xl space-y-3 shadow-md md:min-h-[180px] hover:border-zinc-800 transition-colors">
                                <span className="text-[10px] font-mono text-zinc-500 uppercase block">
                                    {milestone.phase}
                                </span>
                                <h4 className="text-sm font-normal text-white leading-snug">
                                    {milestone.title}
                                </h4>
                                <p className="text-zinc-400 text-[11px] leading-relaxed font-light font-sans">
                                    {milestone.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
